import { createSlice } from "@reduxjs/toolkit";
import { Toastify } from "../../../../components/Toaster";
import { addingSideEffects, deletingSideEffects, errorSideEffects, loadingSideEffects, successSideEffects, updatingSideEffects } from "../..";
import { getOutcomeTrackingState, getRequestTrackingState, MutationTrackingState } from "../../../../services/analytics/tracking";

export interface QuestionData {
  QuestionId: string;
  Question: string;
}

export interface SecurityQuestionsState {
  Data: {
    QuestionData: QuestionData[];
    RenderQuestionCount: number;
  };
  isLoading: boolean;
  isUpdating: boolean;
  isSuccess: boolean;
  isAdding: boolean;
  isDeleting: boolean;
  isError: boolean;
  errorMessage: string | null;
}

export const SecurityQuestionsSlice = createSlice({
  name: "securityquestions",
  initialState: {
    Data: {
      QuestionData: [],
      RenderQuestionCount: 0
    },
    isLoading: false,
    isUpdating: false,
    isAdding: false,
    isSuccess: false,
    isDeleting: false,
    isError: false,
    errorMessage: null
  },
  reducers: {
    getSecurityQuestionsAction(state) {
      Object.assign(state, loadingSideEffects);
    },
    getSecurityQuestionsSuccess(state, action) {
      state.Data.QuestionData = action.payload.Question.Data;
      state.Data.RenderQuestionCount = action.payload.RenderCount.RenderQuestionCount;
      Object.assign(state, successSideEffects);
    },
    getSecurityQuestionsError(state, action) {
      Object.assign(state, errorSideEffects);
      state.errorMessage = action.payload;
    },
    updateSecurityQuestionsAction(state, action) {
      Object.assign(state, updatingSideEffects, getRequestTrackingState(action));
    },
    updateSecurityQuestionsSuccess(state, action) {
      Object.assign(state, successSideEffects, getOutcomeTrackingState(state as MutationTrackingState, "success"));
      const updatedIndex = state.Data.QuestionData.findIndex((q) => q.QuestionId === action.payload.QuestionId);
      if (updatedIndex !== -1) {
        state.Data.QuestionData[updatedIndex].Question = action.payload.Question;
        Toastify("success", "Security Question updated successfully");
      } else {
        Toastify("error", "Security Question not found");
      }
    },
    updateSecurityQuestionsError(state, action) {
      Object.assign(state, errorSideEffects, getOutcomeTrackingState(state as MutationTrackingState, "error"));
      state.errorMessage = action.payload;
      Toastify("error", action.payload);
    },
    addSecurityQuestionsAction(state, action) {
      Object.assign(state, addingSideEffects, getRequestTrackingState(action));
    },
    addSecurityQuestionsSuccess(state, action) {
      Object.assign(state, successSideEffects, getOutcomeTrackingState(state as MutationTrackingState, "success"));
      state.Data.QuestionData = action.payload.QuestionList.Data;
      Toastify("success", "Security Question added successfully");
    },
    addSecurityQuestionsError(state, action) {
      Object.assign(state, errorSideEffects, getOutcomeTrackingState(state as MutationTrackingState, "error"));
      state.errorMessage = action.payload;
      Toastify("error", action.payload);
    },
    deleteSecurityQuestionsAction(state, action) {
      Object.assign(state, deletingSideEffects, getRequestTrackingState(action));
    },
    deleteSecurityQuestionsSuccess(state, action) {
      Object.assign(state, successSideEffects, getOutcomeTrackingState(state as MutationTrackingState, "success"));
      const deletedId = action.payload.questionID;
      state.Data.QuestionData = state.Data.QuestionData?.filter((question: QuestionData) => question.QuestionId !== deletedId);
      state.Data.RenderQuestionCount = Math.min(state.Data.RenderQuestionCount, state.Data.QuestionData.length);
      Toastify("success", "Security Question deleted successfully");
    },
    deleteSecurityQuestionsError(state, action) {
      Object.assign(state, errorSideEffects, getOutcomeTrackingState(state as MutationTrackingState, "error"));
      state.errorMessage = action.payload;
      Toastify("error", action.payload);
    },
    updateSecurityQuestionRenderCountAction(state, action) {
      Object.assign(state, updatingSideEffects, getRequestTrackingState(action));
    },
    updateSecurityQuestionRenderCountSuccess(state, action) {
      Object.assign(state, successSideEffects, getOutcomeTrackingState(state as MutationTrackingState, "success"));
      state.Data.RenderQuestionCount = action.payload.QuestionCount.RenderQuestionCount;
      Toastify("success", "Security Question Settings updated successfully");
    },
    updateSecurityQuestionRenderCountError(state, action) {
      Object.assign(state, errorSideEffects, getOutcomeTrackingState(state as MutationTrackingState, "error"));
      state.errorMessage = action.payload;
      Toastify("error", action.payload);
    }
  }
});

export const {
  getSecurityQuestionsAction,
  getSecurityQuestionsSuccess,
  getSecurityQuestionsError,
  updateSecurityQuestionsAction,
  updateSecurityQuestionsSuccess,
  updateSecurityQuestionsError,
  addSecurityQuestionsAction,
  addSecurityQuestionsSuccess,
  addSecurityQuestionsError,
  deleteSecurityQuestionsAction,
  deleteSecurityQuestionsSuccess,
  deleteSecurityQuestionsError,
  updateSecurityQuestionRenderCountAction,
  updateSecurityQuestionRenderCountSuccess,
  updateSecurityQuestionRenderCountError
} = SecurityQuestionsSlice.actions;

export default SecurityQuestionsSlice.reducer;
